<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'

// Define reactive variables
const areas = ref([])
const loading = ref(true)
const error = ref(null)
const activeAccordion = ref(0) // Track which accordion is open

// Form-related reactive variables
const newArea = ref('')
const newStreet = ref('')
const submitSuccess = ref(null)
const submitError = ref(null)
const unassociatedStreets = ref([])
const selectedArea = ref()
const selectedStreet = ref()

// Toggle accordion
const toggleAccordion = (index) => {
  activeAccordion.value = activeAccordion.value === index ? null : index
}

// Fetch areas and streets
const fetchAreasWithStreets = async () => {
  try {
    loading.value = true
    const response = await axios.get("areas_with_streets_list/")
    areas.value = response.data
    loading.value = false
  } catch (err) {
    error.value = err.message || 'Failed to load areas and streets'
    loading.value = false
    console.error('Error fetching areas:', err)
  }
}

const fetchUnassociatedStreets = async () => {
  try {
    loading.value = true
    const response = await axios.get("unassociated_streets/")
    unassociatedStreets.value = response.data
    loading.value = false
  } catch(err) {
    error.value = err.message || "Failed to load unassociated_streets"
    loading.value = false
    console.error('Error fetching unassociated_streets:', err)
  }
}

const bindAreaAndStreet = async () => {
  try {
    loading.value = true;
    submitSuccess.value = null
    submitError.value = null
    const response = await axios.post("areas_with_streets_list/", {
      area: selectedArea.value,
      street: selectedStreet.value,
    })
    submitSuccess.value = response.data
    await fetchAreasWithStreets()
    await fetchUnassociatedStreets()
  } catch (err) {
    submitError.value = err.response?.data?.error || 'Failed to bind area and street'
    console.error("Error while binding area with street:", err)
  } finally {
    loading.value = false
  }
}

// Fetch data when component mounts
onMounted(() => {
  fetchAreasWithStreets()
  fetchUnassociatedStreets()
})
</script>

<template>
  <div class="content-wrapper">
    <div class="container-fluid" style="min-height: 100vh; margin-bottom: 8vh">
      <div class="container">
        <h2 class="text-center mb-4">Участки и улицы</h2>

        <!-- Add Area and Street Form -->
        <div class="card mb-4">
          <div class="card-body">
            <form @submit.prevent="bindAreaAndStreet">
              <div class="row">
                <div class="col-md-5 mb-3">
                  <label for="areaInput" class="form-label">Участок</label>
                  <select class="form-select" v-model="selectedArea">
                    <option v-for="area in areas" :value="area.area_id">
                      {{ area.area_name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-5 mb-3">
                  <label for="streetInput" class="form-label">Название улицы</label>
                  <select class="form-select" v-model="selectedStreet">
                    <option
                        v-for="(street, index) in unassociatedStreets"
                        :key="index"
                        :value="street.id"
                    >
                      {{ street.name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-2 d-flex align-items-center">
                  <button
                      type="submit"
                      class="btn btn-primary w-100"
                      :disabled="loading"
                  >
                    <span
                        v-if="loading"
                        class="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                    ></span>
                    Добавить
                  </button>
                </div>
              </div>
            </form>

            <!-- Success and Error Alerts -->
            <div v-if="submitSuccess" class="alert alert-success mt-3">
              Successfully added {{ submitSuccess.area.name }} area with {{ submitSuccess.street.name }} street
            </div>
            <div v-if="submitError" class="alert alert-danger mt-3">
              {{ submitError }}
            </div>
          </div>
        </div>

        <!-- Areas and Streets List -->
        <div v-if="loading" class="text-center">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <div v-else-if="error" class="alert alert-danger">
          {{ error }}
        </div>

        <div v-else class="accordion">
          <div v-for="(area, index) in areas" :key="area.area_id" class="accordion-item">
            <h2 class="accordion-header">
              <button
                  class="accordion-button"
                  :class="{ 'collapsed': activeAccordion !== index }"
                  type="button"
                  @click="toggleAccordion(index)"
              >
                {{ area.area_name }}
              </button>
            </h2>
            <div
                class="accordion-collapse"
                :class="{ 'show': activeAccordion === index }"
            >
              <div class="accordion-body">
                <ul class="list-group">
                  <li
                      v-for="street in area.streets"
                      :key="street.street_id"
                      class="list-group-item"
                  >
                    {{ street.street_name }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.accordion-collapse {
  overflow: hidden;
  transition: height 0.3s ease;
  height: 0;
}

.accordion-collapse.show {
  height: auto;
}
</style>